import UserIcon from "@DSG/Icons/UserIcon";
import Info from "@DSG/MobiusOverrides/Icons/Info";
import colorPallete, { bodyFonts, headlineFonts, labelFonts } from "@DSG/MobiusOverrides/styles/variables";
import Tooltip, { TooltipPresentationProps } from "@DSG/MobiusOverrides/Tooltip/Tooltip";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { GetAccountsApiParameter } from "@insite/client-framework/Services/AccountService";
import { GetOrdersApiParameter } from "@insite/client-framework/Services/OrderService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getAccountsDataView } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import LoadAccounts from "@insite/client-framework/Store/Data/Accounts/Handlers/LoadAccounts";
import loadOrders from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrders";
import { getOrdersDataView } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateSearchFields from "@insite/client-framework/Store/Pages/Addresses/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import CardListHeading from "@insite/content-library/Components/CardListHeading";
import OrderSummaryCard, { OrderSummaryCardStyles } from "@insite/content-library/Components/OrderSummaryCard";
import UserSetupPageTypeLink from "@insite/content-library/Components/UserSetupPageTypeLink";
import DataTable from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    numberOfRecords = "numberOfRecords",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.customCSS]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        accountsDataView: getAccountsDataView(state, state.pages.userList.getAccountsParameter),
        userAdministrationPageLink: getPageLinkByPageType(state, "UserAdministrationPage"),
        settingsCollection: getSettingsCollection(state),
        canViewOrders: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
        getAccountsParameter: state.pages.userList.getAccountsParameter,
    };
};

const mapDispatchToProps = {
    LoadAccounts,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentOrdersStyles {
    recentOrderContainer?: GridItemProps;
    cardList?: CardListStyles;
    errorGridItem?: GridItemProps;
    errorText?: TypographyPresentationProps;
    noOrdersFoundGridItem?: GridItemProps;
    dataGridItemHead?: GridItemProps;
    dataGridItem?: GridItemProps;
    noOrdersFoundText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    orderSummaryCard?: OrderSummaryCardStyles;
    cardTitleContainer?: GridItemProps;
    dataGridItemChild?: GridItemProps;
    userRoleGridItem?: GridItemProps;
    rolesTooltipHeader?: TooltipPresentationProps;
    rolesTooltip?: TooltipPresentationProps;
    dataGridItemUserName?: GridItemProps;
    userRolesWrapper?: InjectableCss;
}

export const recentOrdersStyles: RecentOrdersStyles = {
    errorGridItem: { width: 12 },
    dataGridItemChild: {
        width: 2,
        css: css`
            font-size: 14px;
            padding: 0;
            align-items: center;
            svg {
                margin-right: 3px;
            }
        `,
    },
    userRoleGridItem: {
        width: 4,
        css: css`
            display: flex;
            align-items: center;
            font-size: ${bodyFonts.body2RegularLg.fontSize};
            line-height: ${bodyFonts.body2RegularLg.lineHeight};

            &:not([data-test-selector="userListUsersTable_roleHeader"]) {
                display: flex;
                flex-direction: row;
                row-gap: 8px;
                padding: 0;
            }
            
            &[data-test-selector="userListUsersTable_cell_role"] {
                flex-direction: column;
                align-items: flex-start;
            }

            ${IconWrapper} {
                margin-right: 4px;
            }

            [data-test-selector="tooltip_TooltipInfo"] {
                position: relative;
                z-index: 9;
            }
        `,
    },
    dataGridItemUserName: {
        width: 6,
        css: css`
            font-size: 14px;
            padding: 0;
            align-items: center;
            [class*="StyledButton"] {
                text-decoration: underline;
            }
            svg {
                margin-right: 3px;
            }
        `,
    },
    noOrdersFoundGridItem: { width: 12 },
    dataGridItemHead: { 
        width: 12,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    null,
                    css`
                        > div {
                            padding-right: 5px !important;
                        }
                    `,
                ],
                "min",
            )}
        `
    },
    dataGridItem: {
        width: 12,
        css: css`
            border-bottom: 1px solid ${colorPallete.grey300};
            padding: 16px 24px !important;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    null,
                    css`
                        > div {
                            padding-right: 5px;
                        }
                    `,
                ],
                "min",
            )}
        `,
    },
    cardTitleContainer: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: row;
            gap: 15px;
            align-items: center;
            margin-top: 40px;
            padding-bottom: 24px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            margin-top: 30px;
                            padding-bottom: 15px;
                            gap: 0;
                            span {
                                svg {
                                    width: 30px;
                                    height: 30px;
                                }
                            }
                        `,
                    ],
                    "max",
                )}
            h2 {
                margin: 0;
                font: ${headlineFonts.headline3SemiBoldSm.fontWeight} ${headlineFonts.headline3SemiBoldSm.fontSize} /
                    ${headlineFonts.headline3SemiBoldSm.lineHeight} ${headlineFonts.headline3SemiBoldSm.fontFamily};
                color: ${colorPallete.grey800};
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                font-size: ${headlineFonts.headline3SemiBoldLg.fontSize};
                                line-height: ${headlineFonts.headline3SemiBoldLg.lineHeight};
                            `,
                        ],
                        "min",
                    )}
            }
            [class*="GridItemStyle"] {
                justify-content: space-between;
                max-width: 100%;
                flex-basis: 100%;
                padding: 0;
                align-items: center;
            }
            [class*="StyledButton"] {
                color: ${colorPallete.primaryRed500};
                font-weight: 600;
                text-decoration: underline;
                span {
                    color: ${colorPallete.primaryRed500};
                    text-decoration: underline;
                    font-size: 14px;
                    &:hover {
                        color: ${colorPallete.primaryMaroon500};
                    }
                }
            }
        `,
    },
    recentOrderContainer: {
        css: css`
            max-width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        overflow-y: auto;
                    `,
                ],
                "max",
            )}
            & > {
                [class*="GridOffset"] {
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                null,
                                null,
                                css`
                                    width: 1000px !important;
                                    max-width: 1000px;
                                `,
                            ],
                            "max",
                        )}
                }
            }

            /* width */
            ::-webkit-scrollbar {
                width: 6px;
                height: 6px;
            }

            /* Track */
            ::-webkit-scrollbar-track {
                box-shadow: inset 0 0 3px ${colorPallete.grey400};
                border-radius: 6px;
            }

            /* Handle */
            ::-webkit-scrollbar-thumb {
                background: ${colorPallete.grey800};
                border-radius: 6px;
                cursor: pointer;
            }

            /* Handle on hover */
            ::-webkit-scrollbar-thumb:hover {
                background: ${colorPallete.grey200};
            }
        `,
    },
    userRolesWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: flex-start;
        `,
    },
    rolesTooltipHeader: {
        cssOverrides: {
            tooltipContainer: css`
                width: 265px;
                [data-id="tooltipBody"] {
                    padding: 8px 16px;

                    p {
                        font-size: ${labelFonts.label1Regular.fontSize};
                        line-height: ${labelFonts.label1Regular.lineHeight};
                        font-weight: normal;
                    }
                }
                &.showOnBottom {
                    margin-top: 4px !important;
                    margin-left: -1px;
                }
            `,
            tooltipArrow: css`
                display: none;
            `,
        },
    },
    rolesTooltip: {
        cssOverrides: {
            tooltipArrow: css`
                display: none;
            `,
            tooltipWrapper: css`
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    css`
                        [class*="TooltipClickable"] {
                            padding-left: 0;
                            margin-left: -2px;
                        } 
                    `,
                ], "min")}
            `,
            tooltipContainer: css`
                width: 342px;
                margin-bottom: 24px;
                margin-left: -3px;
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        width: 390px;
                    `,
                ], "min")}
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        margin-left: 25px;
                    `,
                ], "max")}

                [data-id="tooltipBody"] {
                    position: relative;
                    padding: 16px 48px 16px 16px;

                    p {
                        font-size: ${labelFonts.label1Regular.fontSize};
                        line-height: ${labelFonts.label1Regular.lineHeight};
                        font-weight: normal;
                    }

                    .close-button {
                        position: absolute;
                        right: 16px;
                        top: 16px;
                    }
                }
            `,
        },
    },
};

const defaultCustomCss = `.card-list-heading{
}
`;

const userListParameter: GetAccountsApiParameter = {
    searchText: "",
};

const styles = recentOrdersStyles;

class DashboardUserList extends React.Component<Props> {
    componentDidMount() {
        userListParameter.pageSize = this.props.fields.numberOfRecords;
        if (this.props.canViewOrders && !this.props.accountsDataView.value) {
            this.props.LoadAccounts(this.props.getAccountsParameter);
        }
    }

    render() {
        const { accountsDataView, userAdministrationPageLink, settingsCollection, canViewOrders } = this.props;
        if (!canViewOrders || !accountsDataView.value) {
            return null;
        }

        const userAdministrationUrl = userAdministrationPageLink
            ? userAdministrationPageLink.url
            : "/my-account/UserAdministration";

        const customCssWrapper = {
            css: css`
                ${this.props.fields.customCSS}
            `,
        };

        return (
            <StyledWrapper {...customCssWrapper}>
                <CardList
                    css={css`
                        padding-bottom: 50px;
                    `}
                    extendedStyles={styles.cardList}
                    data-test-selector="widget_RecentOrders"
                >
                    <StyledWrapper {...styles.cardTitleContainer}>
                        <Icon src={UserIcon} width={40} height={40} />
                        <GridItem>
                            {accountsDataView.value &&
                                accountsDataView.pagination &&
                                accountsDataView.pagination.totalItemCount > 0 && (
                                    <h2>{`Users (${accountsDataView.pagination.totalItemCount})`}</h2>
                                )}
                            <Link href={userAdministrationUrl}>{translate("View user administration")}</Link>
                        </GridItem>
                    </StyledWrapper>
                    {accountsDataView.value.length === 0 && (
                        <GridItem className="no-account-found-grid-item" {...styles.noOrdersFoundGridItem}>
                            <Typography
                                className="no-acount-found-text"
                                {...styles.noOrdersFoundText}
                                data-test-selector="userAdministrationDashboard_NoOrdersFound"
                            >
                                {translate("No accounts found")}
                            </Typography>
                        </GridItem>
                    )}
                    {accountsDataView.value.length > 0 && (
                        <>
                            <GridContainer {...styles.recentOrderContainer}>
                                <GridItem {...styles.dataGridItemHead} data-test-selector="userDetail_header">
                                    <GridItem
                                        {...styles.dataGridItemChild}
                                        data-test-selector="userListUsersTable_usernameHeader"
                                    >
                                        {translate("First Name")}
                                    </GridItem>
                                    <GridItem
                                        {...styles.dataGridItemChild}
                                        data-test-selector="userListUsersTable_usernameHeader"
                                    >
                                        {translate("Last Name")}
                                    </GridItem>
                                    <GridItem
                                        {...styles.dataGridItemUserName}
                                        data-test-selector="userListUsersTable_nameHeader"
                                    >
                                        {translate("Username")}
                                    </GridItem>
                                    <GridItem
                                        {...styles.dataGridItemChild}
                                        {...styles.userRoleGridItem}
                                        data-test-selector="userListUsersTable_roleHeader"
                                    >
                                        {translate("Role")}
                                    </GridItem>
                                </GridItem>

                                {accountsDataView.value.slice(0, this.props.fields.numberOfRecords).map((account, parentIndex) => (
                                    <GridItem key={account.id} {...styles.dataGridItem}>
                                        <GridItem
                                            {...styles.dataGridItemChild}
                                            data-test-selector="userListUsersTable_cell_name"
                                            data-test-key={account.id}
                                        >
                                            {account.firstName}
                                        </GridItem>
                                        <GridItem
                                            {...styles.dataGridItemChild}
                                            data-test-selector="userListUsersTable_cell_name"
                                            data-test-key={account.id}
                                        >
                                            {account.lastName}
                                        </GridItem>
                                        <GridItem
                                            {...styles.dataGridItemUserName}
                                            data-test-selector="userListUsersTable_cell_username"
                                            data-test-key={account.id}
                                        >
                                            <UserSetupPageTypeLink title={account.userName} userId={account.id} />
                                        </GridItem>
                                        <GridItem
                                            {...styles.dataGridItemChild}
                                            {...styles.userRoleGridItem}
                                            data-test-selector="userListUsersTable_cell_role"
                                            data-test-key={account.id}
                                        >
                                            {(account?.properties["assignedRoles"] || account.role)
                                                .split(",")
                                                .map((role, index) => (
                                                    <StyledWrapper key={`${role}-${index}`} {...styles.userRoleGridItem}>
                                                        {role.toLocaleLowerCase() === "administrator" && (
                                                            <Tooltip
                                                                iconProps={{ src: Info }}
                                                                showTooltipWithCloseIconOnClick={true}
                                                                showOnBottom={parentIndex  === 0 || parentIndex === 1}
                                                                text={
                                                                    siteMessage(
                                                                        "DSG_Account_Administrator_ToolTip",
                                                                    ) as string
                                                                }
                                                                {...styles.rolesTooltip}
                                                            />
                                                        )}
                                                        {role.toLocaleLowerCase() === "billtrust" && (
                                                            <Tooltip
                                                                iconProps={{ src: Info }}
                                                                showTooltipWithCloseIconOnClick={true}
                                                                showOnBottom={parentIndex  === 0 || parentIndex === 1}
                                                                text={
                                                                    siteMessage(
                                                                        "DSG_Account_Billtrust_ToolTip",
                                                                    ) as string
                                                                }
                                                                {...styles.rolesTooltip}
                                                            />
                                                        )}
                                                        {role.toLocaleLowerCase() === "buyer3" && (
                                                            <Tooltip
                                                                iconProps={{ src: Info }}
                                                                showTooltipWithCloseIconOnClick={true}
                                                                showOnBottom={parentIndex  === 0 || parentIndex === 1}
                                                                text={
                                                                    siteMessage("DSG_Account_Buyer3_ToolTip") as string
                                                                }
                                                                {...styles.rolesTooltip}
                                                            />
                                                        )}
                                                        {role.toLocaleLowerCase() === "buyer2" && (
                                                            <Tooltip
                                                                iconProps={{ src: Info }}
                                                                showTooltipWithCloseIconOnClick={true}
                                                                showOnBottom={parentIndex  === 0 || parentIndex === 1}
                                                                text={
                                                                    siteMessage("DSG_Account_Buyer2_ToolTip") as string
                                                                }
                                                                {...styles.rolesTooltip}
                                                            />
                                                        )}
                                                        {role.toLocaleLowerCase() === "buyer1" && (
                                                            <Tooltip
                                                                iconProps={{ src: Info }}
                                                                showTooltipWithCloseIconOnClick={true}
                                                                showOnBottom={parentIndex  === 0 || parentIndex === 1}
                                                                text={
                                                                    siteMessage("DSG_Account_Buyer1_ToolTip") as string
                                                                }
                                                                {...styles.rolesTooltip}
                                                            />
                                                        )}
                                                        {role.toLocaleLowerCase() === "requisitioner" && (
                                                            <Tooltip
                                                                iconProps={{ src: Info }}
                                                                showTooltipWithCloseIconOnClick={true}
                                                                showOnBottom={parentIndex  === 0 || parentIndex === 1}
                                                                text={
                                                                    siteMessage(
                                                                        "DSG_Account_Requisitioner_ToolTip",
                                                                    ) as string
                                                                }
                                                                {...styles.rolesTooltip}
                                                            />
                                                        )}
                                                        {(role.toLocaleLowerCase() !== "administrator" &&
                                                        role.toLocaleLowerCase() !== "billtrust" &&
                                                        role.toLocaleLowerCase() !== "buyer3" &&
                                                        role.toLocaleLowerCase() !== "buyer2" &&
                                                        role.toLocaleLowerCase() !== "buyer1" && 
                                                        role.toLocaleLowerCase() !== "requisitioner") && (
                                                            <Tooltip
                                                                iconProps={{ src: Info }}
                                                                showTooltipWithCloseIconOnClick={true}
                                                                showOnBottom={parentIndex  === 0 || parentIndex === 1}
                                                                text={
                                                                    role
                                                                }
                                                                {...styles.rolesTooltip}
                                                            />
                                                        )}
                                                        {role}
                                                    </StyledWrapper>
                                                ))}
                                        </GridItem>
                                    </GridItem>
                                ))}
                            </GridContainer>
                        </>
                    )}
                </CardList>
            </StyledWrapper>
        );
    }
}

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(DashboardUserList),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Users Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
